import { format, parse, setHours } from "date-fns";
import {
  TimeInContainerMetric,
  WorkingDays,
  WorkingHours,
  Container,
  Layout,
} from "@/types/v2";
import {
  DEFAULT_WORKING_HOURS_END,
  DEFAULT_WORKING_HOURS_START,
} from "@/lib/const";
import { firestoreToDateTime } from "@/lib/utils";

export type CalculateTimeInContainerParams = {
  metric?: TimeInContainerMetric;
  cards?: string[];
  workingHours?: WorkingHours;
  now?: Date;
};

export const calculateTimeInContainer = ({
  metric,
  cards,
  workingHours,
  now = new Date(),
}: CalculateTimeInContainerParams): number => {
  // We are going to default to Monday start time if it exists
  const dayStartTime = parseInt(
    (workingHours
      ? (workingHours[WorkingDays.Monday].startTime ??
        DEFAULT_WORKING_HOURS_START)
      : DEFAULT_WORKING_HOURS_START
    ).split(":")[0],
    10
  );
  const dayEndTime = parseInt(
    (workingHours
      ? (workingHours[WorkingDays.Monday].endTime ?? DEFAULT_WORKING_HOURS_END)
      : DEFAULT_WORKING_HOURS_END
    ).split(":")[0],
    10
  );

  const dayStart = setHours(
    metric
      ? parse(metric.id, "yyyy-MM-dd", now)
      : new Date(now).setHours(0, 0, 0, 0),
    dayStartTime
  );
  const workDayEnd = setHours(
    metric ? parse(metric.id, "yyyy-MM-dd", now) : now,
    dayEndTime
  );
  const timerEnd = workDayEnd > now ? now : workDayEnd;

  const timers =
    metric?.timeInContainer.flatMap((m) =>
      m.cardHistory.map((history) => {
        const enteredAt = firestoreToDateTime(history.enteredAt) as Date;
        const timerEnteredAt = enteredAt > dayStart ? enteredAt : dayStart;
        const exitedAt = firestoreToDateTime(history.exitedAt);
        const timerExitedAt = exitedAt
          ? exitedAt > timerEnd
            ? timerEnd
            : exitedAt
          : timerEnd;

        return {
          ...history,
          enteredAt: timerEnteredAt,
          exitedAt: timerExitedAt,
        };
      })
    ) ?? [];

  // These are cards that were in the container when today started
  const holdOverCards = (
    cards?.filter((card) => !timers.map((t) => t.cardId).includes(card)) ?? []
  ).map((card) => ({
    cardId: card,
    enteredAt: dayStart,
    exitedAt: timerEnd,
  }));

  const durations = [...timers, ...holdOverCards].map(
    (timer) =>
      (timer.exitedAt?.getTime() ?? timer.enteredAt.getTime()) -
      (timer.enteredAt.getTime() ?? timer.enteredAt.getTime())
  );
  const total = durations.reduce((acc, curr) => acc + curr, 0) / 1000;
  return total / durations.length;
};

export const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return "—";
  const totalMinutes = Math.floor(seconds / 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  return `${minutes}m`;
};

export type ContainerMetricsRow = {
  containerId: string;
  containerName: string;
  todaySeconds: number;
  weekSeconds: number;
};

export type BuildContainerMetricsRowsParams = {
  layoutEntries: Layout[];
  containers: Container[];
  metrics: TimeInContainerMetric[];
  workingHours?: WorkingHours;
  now?: Date;
};

const filterMetricToContainer = (
  metric: TimeInContainerMetric,
  containerId: string
): TimeInContainerMetric => ({
  ...metric,
  timeInContainer: metric.timeInContainer.filter(
    (entry) => entry.containerId === containerId
  ),
});

const average = (values: number[]): number =>
  values.reduce((acc, curr) => acc + curr, 0) / Math.max(values.length, 1);

export const buildContainerMetricsRows = ({
  layoutEntries,
  containers,
  metrics,
  workingHours,
  now = new Date(),
}: BuildContainerMetricsRowsParams): ContainerMetricsRow[] => {
  const todayId = format(now, "yyyy-MM-dd");
  const todayMetrics = metrics.filter((metric) => metric.id === todayId);
  const weekMetrics = metrics.filter((metric) => metric.id !== todayId);

  return [...layoutEntries]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .flatMap((entry) => {
      const container = containers.find((c) => c.id === entry.i);
      if (!container) return [];
      const cards = container.tags ?? [];

      // Mirrors useContainerMetrics.getCurrentTime, including the
      // no-metric-yet fallback that synthesizes holdovers from live cards.
      const todayAverages = todayMetrics.map((metric) => {
        const time = calculateTimeInContainer({
          metric: filterMetricToContainer(metric, container.id),
          cards,
          workingHours,
          now,
        });
        return isNaN(time) ? 0 : time;
      });
      const todaySeconds =
        todayAverages.length === 0 && cards.length > 0
          ? calculateTimeInContainer({ cards, workingHours, now })
          : average(todayAverages);

      // Mirrors useContainerMetrics.getWeeklyTime: excludes today, no cards.
      const weekAverages = weekMetrics.map((metric) => {
        const time = calculateTimeInContainer({
          metric: filterMetricToContainer(metric, container.id),
          workingHours,
          now,
        });
        return isNaN(time) ? 0 : time;
      });

      return [
        {
          containerId: container.id,
          containerName: container.displayName ?? container.id,
          todaySeconds,
          weekSeconds: average(weekAverages),
        },
      ];
    });
};
